import { forwardRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  LightModeSemanticColors,
  PopoverView,
  Stack,
  Text,
  TextButton,
  Tooltip,
  useDisclosure,
} from '@enechain/ecloud-designsystem'
import { FaTriangleExclamationSolid } from '@enechain/ecloud-icons'

import { IconOnlyButton } from '~/components/common/IconOnlyButton'
import { iconSize } from '~/constants/iconSize'
import {
  ERROR_CATEGORY_INLINE_KEY,
  ERROR_CATEGORY_LABEL_KEY,
  ERROR_INDICATOR_FALLBACK_INLINE_KEY,
  type PlanRowErrorDetailInput,
  categoryInterpolatedMessage,
  resolveInlineReceptionErrorSummary,
  resolvePlanErrorRow,
} from '~/models/planSubmission/errorDetail'
import { translateInterpolatedMessage } from '~/models/planSubmission/interpolatedMessage'
import type { StatusBadgeVariant } from '~/models/planSubmission/statusBadge'

type Props = {
  /**
   * バッジの警告状態 (作成失敗 / 差戻し / `submitFailed` (SUBMIT_FAILED) /
   * 送信失敗の 4 variant で true)。アイコンの表示可否はこの値のみで決まる
   * (review fix #1) — messages が導出できない場合でも hasWarning が true なら
   * アイコンは常時描画し、内容は汎用 fallback 文言にフォールバックする。
   */
  hasWarning: boolean
  /**
   * ⚠ アイコンの色。 badge の semantic status に合わせるため呼び出し側
   * ([[PlanSubmissionStatusBadge]]) が解決して渡す (本 component は variant を
   * 知らない)。 省略時は従来どおり negative (赤)。
   */
  iconColor?: string
  /**
   * バッジ variant (plan-submit-reject-bp-error-code design 決定 #10)。
   * 差戻し (`rejected`) 行だけ、BP コードが無い時の category / 汎用対処文を
   * ステータス横にインライン常時表示する (AC-PLC-033-1)。他 variant は
   * 従来どおり tooltip のみに留める — Decision 10 は AC-PLC-033-2 /
   * AC-PLC-050 が「ステータス横」を要求していない他 variant までインライン化
   * すると spec を超えると判断した。
   */
  variant: StatusBadgeVariant
  errorDetail: PlanRowErrorDetailInput
}

/**
 * 計画提出ステータスの警告アイコン (作成失敗 / 差戻し / `submitFailed`
 * (SUBMIT_FAILED) / 送信失敗) の詳細を hover で tooltip、click で popover に出す
 * 共有 component (plansubmit-error-detail design AC-PLC-008 / AC-PLC-008-1)。
 *
 * - hover → tooltip (DS `Tooltip`)。AC-PLC-008 の常時インライン表示 + hover 詳細。
 * - click → popover 固定 (DS `PopoverView`、AC-PLC-008-1)。外側の click・Escape・
 *   trigger の再 click で閉じる。開いている間は同じ内容の tooltip を出さない。
 *   配線は [[ErrorDetailTrigger]] を参照。
 * - trigger と popover の中の click は一覧行へ伝播させない (#7733)。行 click
 *   (AC-PLC-004 の詳細編集画面への遷移) で popover の表示ごと画面を離れないため。
 *   行の他の部分の click は遷移する。
 *
 * - tooltip の内容は全エラーの `[code] 名称：説明` 全文 (design gap: この surface の
 *   design が無いため新規 visual を発明せず DS 標準 component で実現。 設計書
 *   Design gaps 節)。
 *
 * アイコンの表示可否は `hasWarning` のみで決まる (review fix #1): 正規の
 * stage-B 差戻し (last_error が NULL) で BA11B006 取得が失敗した行や、 決定
 * #10 の既存差戻し行 (受付エラー詳細を持たない) でも `hasWarning` は true の
 * ままであり、 アイコン自体を消してしまうと「警告状態なのに何も見えない」
 * 退行になる。 `resolvePlanErrorMessages` が空を返す場合は variant 別の汎用
 * fallback 文言 (`ERROR_INDICATOR_FALLBACK_KEY`、plan-submit-reject-bp-error-code
 * design 決定 #8) を表示する。
 *
 * OCCTO 受付エラーコードがある行 (`resolveInlineReceptionErrorSummary` が
 * 値を返すケース) は `⚠ [code] エラー名` を常時インライン表示し、 その部分 +
 * アイコンをまとめて hover/click のトリガーにする (plansubmit-error-detail
 * design 決定 #1/#2/#3。 複数件は先頭 1 件 + 「他N件」、 全件は従来どおり
 * tooltip/popover に表示 — 決定 #4)。
 *
 * BP コードが無い行の対処文インライン表示は variant で分かれる
 * (plan-submit-reject-bp-error-code design 決定 #2/#8/#10、AC-PLC-033-1):
 * 差戻し (`rejected`) は category 対処文、それも無ければ variant 別の汎用
 * 対処文を同じインライン trigger でステータス横に常時表示する。他 variant
 * (作成失敗・`submitFailed` (SUBMIT_FAILED)・送信失敗) は従来どおりアイコン
 * のみ常時表示し、内容は hover/click の tooltip/popover にのみ出す (AC-PLC-033-2 /
 * AC-PLC-050 が「ステータス横」を要求しないため)。
 *
 * 発電販売計画 (対象外 slice) からも import 可能な形にしてある
 * (plansubmit-error-detail design Decision #1: 共有 component 拡張は流用可能な
 * 形にするが、 BFF field 配線は本 slice では行わない)。
 */
export const PlanSubmitErrorIndicator = ({
  hasWarning,
  errorDetail,
  iconColor,
  variant,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const disclosure = useDisclosure()
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)

  if (!hasWarning) {
    return null
  }

  const resolvedRow = resolvePlanErrorRow(errorDetail, variant)
  const messages = resolvedRow.kind === 'messages' ? resolvedRow.messages : []
  const inlineSummary = resolveInlineReceptionErrorSummary(
    errorDetail.receptionErrors,
  )
  const ariaLabel = t('page:planSubmission.errorIndicator.ariaLabel')

  const content = (
    <Stack gap={1}>
      {resolvedRow.kind === 'messages' ? (
        resolvedRow.messages.map((m, i) => (
          <Text
            key={
              m.kind === 'receptionError'
                ? `${errorDetail.receptionErrors[i]?.errorCode ?? 'reception'}-${i}`
                : `category-${m.category}`
            }
            fontSize="sm"
          >
            {m.kind === 'receptionError'
              ? m.text
              : translateInterpolatedMessage(
                  t,
                  categoryInterpolatedMessage(ERROR_CATEGORY_LABEL_KEY, m),
                )}
          </Text>
        ))
      ) : (
        <Text fontSize="sm">{t(resolvedRow.fallbackKey)}</Text>
      )}
    </Stack>
  )

  // 決定 #10: BP コードが無い行の category / 汎用対処文をインライン常時表示
  // するのは差戻し (`rejected`) 行のみ。BP コードが無い時点で `messages` は
  // category 1 件 (`errorCategory` が UNSPECIFIED でない) か空配列のどちらか
  // (`resolvePlanErrorMessages` / `resolveInlineReceptionErrorSummary` が同じ
  // `isOkReceptionErrorCode` フィルタを共有するため)。BP コードがある行は
  // `inlineSummary` が優先され、ここへは来ない (決定 #2: 併記しない・排他)。
  //
  // インラインは **次アクションだけ** を出す (2026-09-17 Des 判断、issue #8283)。
  // tooltip 用の全文キー (状態説明 + 次アクション、40〜50 文字) をそのまま
  // 出すと 220px では状態説明の途中で切れ、 AC-PLC-033-1 が求める
  // 「hover せずに次アクションが分かる」 を満たせない。 全文は `content`
  // (tooltip/popover) 側に残るので情報は失われない。
  const inlineFallback =
    variant === 'rejected'
      ? {
          text:
            messages[0]?.kind === 'category'
              ? translateInterpolatedMessage(
                  t,
                  categoryInterpolatedMessage(
                    ERROR_CATEGORY_INLINE_KEY,
                    messages[0],
                  ),
                )
              : t(ERROR_INDICATOR_FALLBACK_INLINE_KEY[variant]),
          remainingCount: 0,
        }
      : undefined
  const inlineDisplay = inlineSummary ?? inlineFallback

  // 警告アイコン。両 trigger で寸法を揃える。button の accessible name は可視
  // ラベル (TextButton) / `aria-label` (IconButton) が担うため装飾扱い。
  // 色は badge の semantic status に追随する (デザイナー指摘 2026-09-14「黄色い
  // badge の場合は、 ⚠️ アイコンも黄色に揃えたい」)。 本 component は variant を
  // 知らないので呼び出し側 ([[PlanSubmissionStatusBadge]]) が解決して渡す。
  // **ここ 1 箇所に置けば インライン trigger (TextButton の leftIcon) と
  // アイコン trigger (IconButton の中身) の両方に効く。**
  // 省略時は従来どおり negative (赤) で、 main の既存挙動と一致する。
  const warningIcon = (
    <FaTriangleExclamationSolid
      boxSize={iconSize.gridInline}
      color={iconColor ?? LightModeSemanticColors.Text.negativeHigh.value}
      aria-hidden="true"
    />
  )

  // popover を開いている間は同じ内容の tooltip を出さない。 DS `Tooltip` の `isHidden` は
  // Tooltip を外して children だけを返すので、 popover が開いた瞬間に button が remount され、
  // popover の anchor と focus が外れる。 そのため `open` の制御で抑える。
  const tooltipControl = {
    open: isTooltipOpen && !disclosure.open,
    onOpenChange: setIsTooltipOpen,
  }

  const triggerChildren =
    inlineDisplay !== undefined ? (
      // インライン常時表示: 警告アイコン + `[code] エラー名` (複数件は先頭 +
      // 「他N件」) または category / 汎用対処文 (決定 #10、差戻し行のみ)。
      <ErrorDetailTrigger
        leftIcon={warningIcon}
        tooltipContent={content}
        tooltipControl={tooltipControl}
      >
        {/* [DS差異] 列幅は ellipsis で吸収し、全文は hover tooltip / click
            popover に出す (plan-submit-reject-bp-error-code design 決定 #2)。
            design/css/components.css の `.cell-bgname` (可変長テキストを
            inline-block + max-width + overflow hidden + text-overflow ellipsis +
            white-space nowrap で切る確立パターン) に倣う。具体的な max-width は
            一覧・詳細で列幅が異なり design に visual surface が無い design gap
            (設計書 Design gaps 節) のため、デザイナー確認までの暫定値。 */}
        <Text
          as="span"
          display="inline-block"
          verticalAlign="bottom"
          overflow="hidden"
          maxWidth="220px"
          whiteSpace="nowrap"
          textOverflow="ellipsis"
        >
          {inlineDisplay.remainingCount > 0
            ? `${inlineDisplay.text} ${t(
                'page:planSubmission.errorIndicator.moreCount',
                { count: inlineDisplay.remainingCount },
              )}`
            : inlineDisplay.text}
        </Text>
      </ErrorDetailTrigger>
    ) : (
      // IconOnlyButton は受け取った props と ref を DS `IconButton` へ転送するので、
      // `PopoverView` の trigger props もそのまま button に届く ([[ErrorDetailTrigger]] の JSDoc)。
      <IconOnlyButton
        description={content}
        label={ariaLabel}
        size="xs"
        tooltipControl={tooltipControl}
        variant="ghost"
      >
        {warningIcon}
      </IconOnlyButton>
    )

  // popover の trigger と body はどちらも一覧行の内側の React ツリーにあり、その
  // click は行の onClick (AC-PLC-004 の行遷移) へ伝播する。trigger の click で
  // popover を開いた後の body の操作 (文言の選択を含む) でも画面を離れないよう、
  // PopoverView 全体で伝播を止める (#7733、[[GcExcludedKomaMark]] と同じ pattern)。
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- ユーザー操作を捕捉する要素ではなく、trigger / popover の click 合成イベントが一覧行へ伝播するのを止めるだけの wrapper
    <span onClick={(e) => e.stopPropagation()}>
      <PopoverView
        bodyChildren={content}
        disclosure={disclosure}
        placement="bottom-start"
        triggerChildren={triggerChildren}
      />
    </span>
  )
}

type ErrorDetailTriggerProps = Omit<
  React.ComponentPropsWithoutRef<'button'>,
  'children'
> & {
  children: React.ReactElement
  leftIcon?: React.ReactElement
  tooltipContent: React.ReactNode
  tooltipControl: { open: boolean; onOpenChange: (open: boolean) => void }
}

/**
 * hover tooltip と click popover の両方の trigger になる、 可視ラベル付きの button。
 *
 * DS `PopoverView` は trigger props (onClick・aria-expanded・anchor ref) を
 * `triggerChildren` の直下の要素に `asChild` で渡す。直下に DS `Tooltip` を置くと
 * DS `Tooltip` はそれを DOM を描かない `Tooltip.Root` に流すため、button に届かず
 * popover が開かない。そこで直下にこの component を置き、受け取った trigger props
 * と ref を DS `Tooltip` の中の button へ自分で渡す。DS `Tooltip` の内側の
 * `Tooltip.Trigger asChild` が自分の props を button の props に merge するので、
 * button は両方の trigger props を持つ。
 */
const ErrorDetailTrigger = forwardRef<
  HTMLButtonElement,
  ErrorDetailTriggerProps
>(function ErrorDetailTrigger(
  { tooltipContent, tooltipControl, ...buttonProps },
  ref,
) {
  // [規約逸脱] 可視ラベル (`[code] エラー名`) を持つ button に Tooltip を重ねる。
  // tooltip が出すのは可視ラベルの再掲ではなく AC-PLC-008 の「詳細説明」
  // (全エラーの `[code] 名称：説明` 全文) で、インライン要約 (先頭 1 件の
  // code + 名称) とは内容が異なるため。
  //
  // [DS差異] TextButton recipe の 2 点を override する: (1) `color` =
  // TextButton 既定の link 色ではなくエラー表示のため negative-high に、
  // (2) `paddingX={0}` = ステータスバッジ横に詰めて併記するため (行内配置で
  // DS 既定の水平 padding だと過剰な余白が入る)。インライン code+name /
  // 対処文の visual surface は design に無く (design gap)、最終的な visual
  // detail はデザイナー確認で確定する。
  return (
    <Tooltip
      content={tooltipContent}
      // app の moduleResolution (node10) では chakra が参照する `@ark-ui/react/*` の
      // 型が解決できず details が any になるため、使う field だけ型を書く。
      onOpenChange={(details: { open: boolean }) => {
        tooltipControl.onOpenChange(details.open)
      }}
      open={tooltipControl.open}
    >
      <TextButton
        {...buttonProps}
        ref={ref}
        color={LightModeSemanticColors.Text.negativeHigh.value}
        paddingX={0}
        size="xs"
      />
    </Tooltip>
  )
})
